import { Box, Typography, alpha } from "@mui/material";
import { Check } from "@mui/icons-material";
import { ReactNode } from "react";

export interface OptionCardItem {
  value: string;
  label: ReactNode;
  sub: string;
  icon: ReactNode;
  /** data-testid de la card, para pruebas automatizadas. */
  testId: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: OptionCardItem[];
}

/**
 * Replica `.radio-card` de styles.css (sección "premium fintech", la que gana en
 * cascada): tile de icono 40×40 que se invierte al seleccionar y círculo de check
 * a la derecha.
 *
 * Convive con `RadioCardGroup`, que quedó con el spec viejo (sin tile ni check) y
 * sigue en Cotizador, ConexionCuentas y ExpInformacionSolicitud.
 */
export const OptionCardGroup = ({ value, onChange, options }: Props) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
    {options.map((opt) => {
      const on = value === opt.value;
      return (
        <Box
          key={opt.value}
          onClick={() => onChange(opt.value)}
          data-testid={opt.testId}
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            gap: "16px",
            p: "16px 18px",
            borderRadius: "10px",
            border: "1.5px solid",
            borderColor: on ? "primary.main" : "#E4E7EC",
            bgcolor: on ? alpha(theme.palette.primary.main, 0.06) : "background.paper",
            // El inset simula el borde de 2px del estado activo sin recalcular el layout.
            boxShadow: on ? `inset 0 0 0 1px ${theme.palette.primary.main}` : "none",
            cursor: "pointer",
            userSelect: "none",
            transition: "border-color .18s ease, background-color .18s ease, box-shadow .2s ease",
            "&:hover": {
              borderColor: on ? "primary.main" : alpha(theme.palette.primary.main, 0.5),
              bgcolor: alpha(theme.palette.primary.main, 0.06),
            },
          })}
        >
          <Box
            sx={(theme) => ({
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: "9px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid",
              borderColor: on ? "primary.main" : "#E4E7EC",
              bgcolor: on ? "primary.main" : "#F4F6F9",
              color: on ? "#FFF" : "text.disabled",
              boxShadow: on ? `0 4px 12px -2px ${alpha(theme.palette.primary.main, 0.4)}` : "none",
              transition: "background-color .2s ease, color .2s ease, border-color .2s ease",
              // El tamaño lo fija el tile para que el consumidor pase el icono sin props extra.
              "& > svg": { fontSize: 20 },
            })}
          >
            {opt.icon}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
              {opt.label}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.disabled", mt: "2px", lineHeight: 1.5 }}>
              {opt.sub}
            </Typography>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              width: 22,
              height: 22,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1.5px solid",
              borderColor: on ? "primary.main" : "#D6DBE3",
              bgcolor: on ? "primary.main" : "background.paper",
              color: "#FFF",
              transition: "background-color .2s ease, border-color .2s ease",
            }}
          >
            {on && <Check sx={{ fontSize: 12 }} />}
          </Box>
        </Box>
      );
    })}
  </Box>
);
